import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  NavigationPageItemDto,
  TextFromTableDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import { InlineAlert } from '@asset-metrix/portal-ui';
import { Skeleton } from '@mantine/core';

import { useTextFromTableData } from './useTextFromTableData';

export type TextFromTableProps = {
  entity: TextFromTableDto;
  widgetId: WidgetDto['id'];
  pageId: NavigationPageItemDto['pageId'];
  pageFilterInstanceId: string;
};

/**
 * Component that renders text with placeholders filled from datatable data
 */
export const TextFromTable: FC<TextFromTableProps> = ({
  entity,
  widgetId,
  pageId,
  pageFilterInstanceId,
}) => {
  const { t } = useTranslation();
  const { text, tableKey, columns, sortColumns } = entity.config;

  const { formattedText, isLoading, error } = useTextFromTableData({
    text,
    tableKey,
    columns,
    sortColumns,
    pageId,
    widgetId,
    pageFilterInstanceId,
  });

  // Show skeleton while loading
  if (isLoading) {
    return (
      <Skeleton
        height={20}
        width="70%"
        data-testid="text-from-table-skeleton"
      />
    );
  }

  // Show error message if data fetch failed or placeholders cannot be resolved
  if (error) {
    let errorMessage: string;

    switch (error.type) {
      case 'FETCH_ERROR':
        errorMessage = t('app.textFromTableWidget.errorLoadingData');
        break;
      case 'MISSING_COLUMN_CONFIG':
        errorMessage = t('app.textFromTableWidget.missingColumnConfig', {
          keys: error.details?.join(', ') ?? '',
        });
        break;
      case 'NO_DATA_FOUND':
        errorMessage = t('app.textFromTableWidget.noDataFound');
        break;
      default:
        errorMessage = t('app.textFromTableWidget.errorLoadingData');
    }

    return (
      <InlineAlert showIcon={error.type !== 'NO_DATA_FOUND'}>
        {errorMessage}
      </InlineAlert>
    );
  }

  return <p data-testid="text-from-table-content">{formattedText}</p>;
};
